import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { getClassNameFactory, getRehydratableName, Grid } from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { Dropdown } from '@dcl/ui';
import { ICascadingDropdownRef, ICascadingDropdown } from './types';

export const CascadingDropdown = forwardRef<
  ICascadingDropdownRef,
  ICascadingDropdown
>((props, ref) => {
  const getClassName = getClassNameFactory(CascadingDropdown.displayName);

  const { dependentDropdowns } = props;

  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string;
  }>({});

  const handleChange = (dropdownName: string, value: string) => {
    setSelectedValues((prevSelectedValues) => ({
      ...prevSelectedValues,
      [dropdownName]: value,
    }));
  };

  const isDropdownEnabled = (dropdown: {
    name?: string;
    options?: object | string[];
    dependencies: any;
  }) => {
    return (
      dropdown.dependencies.length === 0 ||
      dropdown.dependencies.every(
        (dep: string | number) =>
          selectedValues[dep] && selectedValues[dep] !== ''
      )
    );
  };

  const getTransformedDropdownOptions = (
    optionsList: string[]
  ): { label: string; value: string }[] => {
    return optionsList.map((option: string) => {
      return { label: option, value: option };
    });
  };

  const getDropdownOptions = (dropdown: {
    name?: string;
    options: any;
    dependencies: any;
  }) => {
    if (dropdown.dependencies.length > 0) {
      const dependentParent = dropdown.dependencies[0];

      const optionsList =
        (dropdown.options as { [key: string]: any[] })[
          selectedValues[dependentParent]
        ] || [];

      return getTransformedDropdownOptions(optionsList);
    }

    return getTransformedDropdownOptions(dropdown.options);
  };

  const filterFn = (
    items: { label: string; value?: React.ReactNode }[],
    value: React.ReactNode
  ) => {
    if (typeof value !== 'string') {
      return [];
    }

    const label = value.toLowerCase();

    return items.filter((item: { label: string }) =>
      item.label.toLowerCase().includes(label)
    );
  };

  const getSelectedValues = () => {
    return selectedValues;
  };

  useImperativeHandle(ref, () => ({
    getSelectedValues,
  }));

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(CascadingDropdown.displayName)}
      {...propsToDataAttributes(props)}
    >
      <div>
        {dependentDropdowns.map((dropdown) => (
          <Grid.Item key={dropdown.name}>
            <div key={dropdown.name}>
              <label
                className={
                  isDropdownEnabled(dropdown)
                    ? ''
                    : getClassName({ descendantName: 'disabledLabel' })
                }
              >
                {dropdown.name}:{' '}
              </label>
              <Dropdown
                filterFunction={filterFn}
                maxVisibleItems={5}
                block={true}
                items={getDropdownOptions(dropdown)}
                onValueChanged={(value: React.ReactNode) =>
                  handleChange(dropdown.name, value as string)
                }
                placeholder=""
                removeAutoFocus
                className={
                  isDropdownEnabled(dropdown)
                    ? ''
                    : getClassName({ descendantName: 'disabled' })
                }
              />
            </div>
            <br />
          </Grid.Item>
        ))}
      </div>
    </div>
  );
});

CascadingDropdown.displayName = 'CascadingDropdown';
